/**
 * Shared URL parsing constants and utilities for dev-proxy URL routing.
 * Used by both dev-proxy (server-side) and dev-ui (client-side).
 */

/** Service name pattern: lowercase letter followed by lowercase alphanumeric + hyphens. */
export const SERVICE_NAME_PATTERN: RegExp = /^[a-z][a-z0-9-]*$/;

/** URL pattern for /<service>/<worktree>/... paths */
const SERVICE_WORKTREE_PATTERN = /^\/([a-z][a-z0-9-]*)\/([^/]+)/;

/**
 * Parse service and worktree from a URL path like /service/worktree/...
 * Returns null for non-matching URLs.
 */
export function parseServiceAndWorktree(
  pathname: string
): { service: string; worktree: string } | null {
  const match = pathname.match(SERVICE_WORKTREE_PATTERN);
  if (!match?.[1] || !match?.[2]) return null;
  try {
    return { service: decodeURIComponent(match[1]), worktree: decodeURIComponent(match[2]) };
  } catch {
    return { service: match[1], worktree: match[2] };
  }
}

/** Response shape for the /_service proxy identity endpoint. */
export interface ServiceIdentityResponse {
  service: string;
  worktree: string;
  port: number;
  proxied: boolean;
  /** True when the dev-proxy is running on a cloud node (FPO3_CLOUD_NODE is set). */
  cloudNode?: boolean;
}

export interface DevProxyRouteWorktree {
  readonly worktree: string;
  readonly port: number;
}

export interface DevProxyRoutesPayload {
  readonly worktrees: readonly DevProxyRouteWorktree[];
}

/** Parse and validate a `/api/routes/<service>` dev-proxy response payload. */
export function parseDevProxyRoutesPayload(payload: unknown): DevProxyRoutesPayload | null {
  if (payload == null || typeof payload !== 'object') return null;
  const worktrees = (payload as { worktrees?: unknown }).worktrees;
  if (!Array.isArray(worktrees)) return null;

  const parsed: DevProxyRouteWorktree[] = [];
  for (const entry of worktrees) {
    if (entry == null || typeof entry !== 'object') return null;
    const candidate = entry as { worktree?: unknown; port?: unknown };
    if (typeof candidate.worktree !== 'string' || candidate.worktree.trim() === '') return null;
    if (
      typeof candidate.port !== 'number' ||
      !Number.isInteger(candidate.port) ||
      candidate.port < 1 ||
      candidate.port > 65_535
    ) {
      return null;
    }
    parsed.push({ worktree: candidate.worktree, port: candidate.port });
  }
  return { worktrees: parsed };
}

/**
 * Build a dev-proxy redirect path for a service+worktree pair.
 * Encodes both segments for safe URL routing.
 */
export function buildServiceWorktreePath(
  service: string,
  worktree: string,
  pathname = '/',
  search = ''
): string {
  return `/${encodeURIComponent(service)}/${encodeURIComponent(worktree)}${pathname}${search}`;
}

/**
 * Parse a raw `DEV_PROXY_PORT` string into a valid port number, or `null` when
 * the value is absent, empty, non-numeric (including partial-numeric strings
 * such as `'4444abc'`), or out of the valid TCP port range 1–65535. Callers
 * apply their own context-specific default (e.g. `?? 3333`, `?? 8080`) —
 * `null` distinguishes "unset" from "explicitly set to that same value".
 */
export function parseDevProxyPort(raw: string | undefined | null): number | null {
  const trimmed = raw?.trim();
  if (trimmed == null || trimmed === '') return null;
  // Reject partial-numeric strings (e.g. '4444abc') by requiring the whole
  // string to be digits before parsing.
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 65_535 ? parsed : null;
}

/** Laptop prefix used on local machines to distinguish local tabs at a glance.
 *  Cloud-node tabs get no machine prefix — the hostname already makes the
 *  remote context obvious. */
const LOCAL_MACHINE_PREFIX = '💻 ';

/**
 * Build the worktree title prefix.
 * Shared between dev-proxy title-injector (server) and DevPill (client).
 *
 * The 💻 prefix is applied on local machines only; cloud-node tabs get no
 * machine indicator (the hostname makes it clear).
 *
 * When `skipWorktreeName` is true, returns only the machine indicator (or an
 * empty string on cloud nodes). Used by services that manage their own
 * worktree display (e.g. wt-web).
 */
export function buildTitlePrefix(
  worktreeName: string,
  isCloudNode: boolean,
  options?: { skipWorktreeName?: boolean }
): string {
  const machinePrefix = isCloudNode ? '' : LOCAL_MACHINE_PREFIX;
  if (options?.skipWorktreeName) {
    return machinePrefix;
  }
  return `${machinePrefix}[${worktreeName}] `;
}
